import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import { tasksService, TaskCreate } from '../services/tasks'
import { projectsService, Project } from '../services/projects'
import { getErrorMessage } from '../utils/apiTransformers'
import { UserPicker } from '../components/UserPicker'
import { ProjectPicker } from '../components/ProjectPicker'
import { useTranslation } from '../i18n/I18nContext'
import { useStatusOptions } from '../hooks/useStatusOptions'
import type { ApiErrorLike } from '../utils/apiError'

export default function CreateTask() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  // Part 16 — pull task status options from the org's set (or canonical fallback).
  const { options: statusOptions } = useStatusOptions('task')
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const projectIdFromUrl = searchParams.get('project_id')
  const [formData, setFormData] = useState<TaskCreate>({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    project_id: projectIdFromUrl ? parseInt(projectIdFromUrl) : 0,
    assignee_id: undefined,
    start_date: '',
    due_date: '',
    estimated_hours: undefined,
  })

  useEffect(() => {
    fetchProjects()
  }, [])

  useEffect(() => {
    if (projectIdFromUrl && projects.length > 0) {
      const projectId = parseInt(projectIdFromUrl)
      if (projects.some(p => p.id === projectId)) {
        setFormData((prev) => ({ ...prev, project_id: projectId }))
      }
    }
  }, [projectIdFromUrl, projects])

  const fetchProjects = async () => {
    try {
      const data = await projectsService.list()
      setProjects(data)
      // Set first project as default if available
      if (data.length > 0 && formData.project_id === 0) {
        setFormData((prev) => ({ ...prev, project_id: data[0].id }))
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
      toast.error('Failed to load projects')
    }
  }

  const handleChange = (field: keyof TaskCreate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value
    setFormData((prev) => {
      if (field === 'project_id') {
        return { ...prev, [field]: parseInt(value) || 0 }
      }
      if (field === 'estimated_hours') {
        return { ...prev, [field]: value ? parseFloat(value) : undefined }
      }
      if (field === 'assignee_id') {
        return { ...prev, [field]: value ? parseInt(value) : undefined }
      }
      return { ...prev, [field]: value }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        toast.error('Task title is required')
        setLoading(false)
        return
      }

      if (!formData.project_id || formData.project_id === 0) {
        toast.error('Please select a project')
        setLoading(false)
        return
      }

      // Prepare data for API
      const taskData: TaskCreate = {
        title: formData.title.trim(),
        description: formData.description?.trim() || undefined,
        status: formData.status,
        priority: formData.priority,
        project_id: formData.project_id,
        assignee_id: formData.assignee_id,
        start_date: formData.start_date || undefined,
        due_date: formData.due_date || undefined,
        estimated_hours: formData.estimated_hours,
      }

      const task = await tasksService.create(taskData)
      toast.success('Task created successfully!')
      navigate(`/task/${task.id}`)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error creating task:', error)
      toast.error(getErrorMessage(error) || 'Failed to create task. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl mb-2">{t('tasks.create.title')}</h1>
              <p className="text-[#bcccdc]">Fill in the details below to create a new task</p>
            </div>
            <button
              onClick={() => navigate('/task')}
              className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] border border-[#829ab1] rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tasks
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-8">
          <div className="space-y-6">
            {/* Task Title */}
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                {t('tasks.fields.title')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={handleChange('title')}
                placeholder={t('tasks.fields.title.placeholder')}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                required
              />
            </div>

            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                {t('tasks.fields.project')} <span className="text-red-500">*</span>
              </label>
              <ProjectPicker
                projects={projects}
                value={formData.project_id}
                onChange={(projectId) =>
                  setFormData((prev) => ({ ...prev, project_id: projectId }))
                }
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                {t('tasks.fields.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={handleChange('description')}
                placeholder={t('tasks.fields.description')}
                rows={4}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none resize-none text-[#2A3B4C]"
              />
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">{t('tasks.fields.status')}</label>
                <select
                  value={formData.status}
                  onChange={handleChange('status')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                >
                  {statusOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">{t('tasks.fields.priority')}</label>
                <select
                  value={formData.priority}
                  onChange={handleChange('priority')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Start date, due date and estimated hours — start + due draw the bar on the project timeline */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="task-start-date" className="block text-sm font-medium text-[#102a43] mb-2">Start Date</label>
                <input
                  id="task-start-date"
                  type="date"
                  value={formData.start_date}
                  max={formData.due_date || undefined}
                  onChange={handleChange('start_date')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Due Date</label>
                <input
                  type="date"
                  value={formData.due_date}
                  min={formData.start_date || undefined}
                  onChange={handleChange('due_date')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Estimated Hours</label>
                <input
                  type="number"
                  value={formData.estimated_hours || ''}
                  onChange={handleChange('estimated_hours')}
                  placeholder="0.00"
                  min="0"
                  step="0.5"
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                {t('task.assignee.label')}
              </label>
              <UserPicker
                value={formData.assignee_id ?? null}
                onChange={(userId) =>
                  setFormData((prev) => ({ ...prev, assignee_id: userId ?? undefined }))
                }
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4 border-t border-[#bcccdc]">
              <button
                type="button"
                onClick={() => navigate('/task')}
                className="px-6 py-2 border-2 border-[#bcccdc] rounded-lg hover:bg-[#f0f4f8] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.title.trim() || formData.project_id === 0}
                className="px-6 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Create Task
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

